import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthResponse, MessageResponse } from '../_models/responses';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';


@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<AuthResponse | null>;
    private currentOfflineUserSubject: BehaviorSubject<AuthResponse | null>;
    //public currentUser: Observable<User>;
    public currentUser: Observable<AuthResponse | null>;

    private isValidAuthResponse(user: AuthResponse | null | undefined): user is AuthResponse {
        return !!user?.access_token && !!user?.refresh_token;
    }

    private getStoredUser(storageKey: string): AuthResponse | null {
        const storedValue = localStorage.getItem(storageKey);

        if (!storedValue || storedValue === 'undefined') {
            localStorage.removeItem(storageKey);
            return null;
        }

        try {
            const parsedValue = JSON.parse(storedValue) as AuthResponse;

            if (this.isValidAuthResponse(parsedValue)) {
                return parsedValue;
            }
        } catch {
        }

        localStorage.removeItem(storageKey);
        return null;
    }

    constructor(private http: HttpClient, private toastr: ToastrService,
        private router: Router,
        ) {
        this.currentUserSubject = new BehaviorSubject<AuthResponse | null>(this.getStoredUser('currentUser'));
        this.currentOfflineUserSubject = new BehaviorSubject<AuthResponse | null>(this.getStoredUser('offlineUser'));
        this.currentUser = this.currentUserSubject.asObservable();
    }

    // public get currentUserValue(): User {
    //     return this.currentUserSubject.value;
    // }
    public get currentUserValue(): AuthResponse | null {
        return this.currentUserSubject.value;
    }

    public get currentOfflineUserValue(): AuthResponse | null {
        return this.currentOfflineUserSubject!.value;
    }

    refresh() {
        let header = new HttpHeaders();

        if (!this.currentUserValue?.refresh_token) {
            if (!environment.production) {
                console.log('Refresh token not present: ', this.currentUserValue?.refresh_token);
            }
            this.currentUserSubject.next(null);
            localStorage.removeItem('currentUser');
            localStorage.removeItem('offlineUser');

            return throwError(() => new Error('Refresh token not present'));
        }

        header = header.append('Authorization', `Bearer ${this.currentUserValue?.refresh_token}`);

        if (!environment.production) {
            console.log('Before refresh token: ', this.currentUserValue?.refresh_token);
            console.log('Refresh token: ', header);
        }

        return this.http.post<AuthResponse>(`${environment.apiAuthUrl}/auth/token/refresh`, {}, {headers: header})
            .pipe(map(user => {
                if (!this.isValidAuthResponse(user)) {
                    localStorage.removeItem('currentUser');
                    localStorage.removeItem('offlineUser');
                    this.currentUserSubject.next(null);
                    this.currentOfflineUserSubject.next(null);

                    throw new Error('Invalid refresh response');
                }

                localStorage.setItem('currentUser', JSON.stringify(user));
                localStorage.setItem('offlineUser', JSON.stringify(user));
                this.currentUserSubject!.next(user);
                this.currentOfflineUserSubject!.next(user);

                return user;
            }))
    }

    verifyaccount(email: string, token: string) {
        return this.http.get<MessageResponse>(`${environment.apiAuthUrl}/auth/confirm/account/` + email + "/" + token);
    }


    login(email: string, password: string) {
        let header = new HttpHeaders();
        header = header.append('content-type', 'application/json');

        return this.http.post<AuthResponse>(`${environment.apiAuthUrl}/auth/login`, { email, password },{headers : header})
            .pipe(map(user => {
                if (!this.isValidAuthResponse(user)) {
                    throw new Error('Invalid login response');
                }

                localStorage.setItem('currentUser', JSON.stringify(user));
                localStorage.setItem('offlineUser', JSON.stringify(user));
                this.currentUserSubject!.next(user)!;
                this.currentOfflineUserSubject!.next(user)!;

                return user;
            }));
    }

    requestAccountUnlock(email: string) {
        let header = new HttpHeaders();
        header = header.append('content-type', 'application/json');

        const request = {
            email,
            client_id: environment.clientId
        };

        return this.http.post<MessageResponse>(`${environment.apiAuthUrl}/auth/unlock/account`, request, { headers: header });
    }

    logout() {
        // remove user from local storage to log user out
        this.toastr.success('User logged out succesfully','Logout');
        localStorage.removeItem('currentUser');
        localStorage.clear();
        this.currentUserSubject.next(null);
        this.router.navigate(['/login']);
    }

}
